/**
 * useQueryExecution
 * Executes a query against live data (no caching) and downloads CSV exports.
 */
import { useMutation } from '@tanstack/react-query';
import { queryService } from '@services';
import type { ExecuteQueryRequest, QueryResultsResponse } from '@app-types';

/** Build an Excel-safe CSV filename: `{QueryName}_{YYYY-MM-DD}.csv`. */
function buildCsvFilename(queryName: string): string {
  let safe = queryName.replace(/[^\w.-]+/g, '_');
  while (safe.startsWith('_')) safe = safe.slice(1);
  while (safe.endsWith('_')) safe = safe.slice(0, -1);
  if (!safe) safe = 'query';
  return `${safe}_${new Date().toISOString().slice(0, 10)}.csv`;
}

export const useQueryExecution = (id: number) => {
  const executeMutation = useMutation<QueryResultsResponse, Error, ExecuteQueryRequest>({
    mutationFn: (body) => queryService.execute(id, body),
  });

  const downloadCsv = async (queryName: string): Promise<void> => {
    // Backend streams the CSV; we receive it as a Blob and hand it to the browser.
    const blob = await queryService.exportCsv(id);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = buildCsvFilename(queryName);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return {
    execute: executeMutation.mutateAsync,
    results: executeMutation.data,
    isExecuting: executeMutation.isPending,
    error: executeMutation.error,
    reset: executeMutation.reset,
    downloadCsv,
  };
};
